import { useEffect, useRef, useState } from "react";
import { getIntervalAmount } from "../../Functions/incrementAmount";
import {
    AnalyticsBarContainer,
    BarLine,
    InnerBar,
    OuterBar,
    BoundText,
    BarAndLabelContainer,
    BarValueLabel,
    BarLabel,
} from "./BarAnalyticsStyle";

const BarAnalyticsDefaults = {
    percent: 40,
    lowerBound: "0",
    upperBound: "100",
    width: 50,
    height: 20,
    animationTimeBar: 3,
    max: 100,
    showVerticalBar: true,
};
interface BarAnalyticsProps {
    percent?: number;
    lowerBound?: string;
    upperBound?: string;
    width?: number;
    height?: number;
    animationTimeBar?: number;
    max?: number;
    displayValue: boolean;
    label: string;
    showVerticalBar?: boolean;
}

export const BarAnalytics: React.FC<BarAnalyticsProps> = ({
    percent = BarAnalyticsDefaults.percent,
    lowerBound = BarAnalyticsDefaults.lowerBound,
    upperBound = BarAnalyticsDefaults.upperBound,
    width = BarAnalyticsDefaults.width,
    height = BarAnalyticsDefaults.height,
    animationTimeBar = BarAnalyticsDefaults.animationTimeBar,
    max = BarAnalyticsDefaults.max,
    displayValue,
    label,
    showVerticalBar = BarAnalyticsDefaults.showVerticalBar,
}) => {
    const innerBarRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [totalWidth, setTotalWidth] = useState<number>();
    const [boundSpace, setTotalBoundSpace] = useState<number>(0);
    const [labelSpace, setLabelSpace] = useState<number>(0);
    const [barSpace, setTotalBarSpace] = useState<number>(0);
    const [currVal, setCurrVal] = useState<number>(0);

    const intervalGap: number = 50;
    const incremAmount: number = getIntervalAmount({
        propsPercent: percent,
        defaultPercent: BarAnalyticsDefaults.percent,
        propsAnimationTime: animationTimeBar,
        defaultAnimationTime: BarAnalyticsDefaults.animationTimeBar,
        intervalAmount: intervalGap,
    });

    useEffect(() => {
        const valueLoop: NodeJS.Timer = setInterval(() => {
            const tempCurrValue: number = currVal
                ? currVal + incremAmount / 100
                : 0 + incremAmount / 100;
            setCurrVal(tempCurrValue);
        }, intervalGap);
        if (currVal ? currVal >= percent : 0 >= percent) {
            clearInterval(valueLoop);
        }
        return () => {
            clearInterval(valueLoop);
        };
    }, [currVal, incremAmount, percent]);

    useEffect(() => {
        containerRef.current
            ? setTotalWidth(containerRef.current.offsetWidth)
            : setTotalWidth(0);
    }, [containerRef]);

    useEffect(() => {
        totalWidth ? setTotalBarSpace(totalWidth * 0.6) : setTotalBarSpace(0);
        totalWidth
            ? setTotalBoundSpace(totalWidth * 0.075)
            : setTotalBoundSpace(0);
        totalWidth ? setLabelSpace(totalWidth * 0.25) : setLabelSpace(0);
    }, [totalWidth]);

    return (
        <AnalyticsBarContainer
            ref={containerRef}
            $width={width}
            $height={height}
        >
            <BarLabel $width={labelSpace}>{label}</BarLabel>
            <BoundText $width={boundSpace}>{lowerBound}</BoundText>
            <BarAndLabelContainer>
                <BarValueLabel
                    $finalWidth={percent}
                    $animationTime={animationTimeBar}
                >
                    {displayValue ? Math.round(currVal * (max / 100)) : ""}
                </BarValueLabel>
                <BarLine
                    $finalWidth={percent}
                    $animationTime={animationTimeBar}
                    $barLine={showVerticalBar}
                />
                <OuterBar $width={barSpace}>
                    <InnerBar
                        ref={innerBarRef}
                        $animationTime={animationTimeBar}
                        $width={percent}
                    />
                </OuterBar>
            </BarAndLabelContainer>
            <BoundText $width={boundSpace}>{upperBound}</BoundText>
        </AnalyticsBarContainer>
    );
};
